import { FC, Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { CheckCircleIcon, ExclamationCircleIcon } from "@heroicons/react/24/solid";
import Button from "../common/Button";
import LoadingSpinner from "../common/LoadingSpinner";
import Textarea from "../common/Textarea";
import Alert from "../common/Alert";
import { getMailTemplate } from "../../api/getMailTemplate";
import { postNotificationDocsNotify } from "../../api/postNotificationDocsNotify";

type SendResult = "success" | "failure" | null;

const DEFAULT_FAILURE_MESSAGE =
  "送信処理を受け付けられませんでした。\n時間をおいて再度お試しください。";

const TEMPLATE_KEY = "ESTIMATED_AMOUNT_NOTIFY_INDIVIDUAL";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  notificationDocNo: string;
  yearMonth: string; // "YYYYMM"形式
  agencyName: string;
  onSent?: () => void;
};

const replaceAllPlaceholder = (
  text: string,
  placeholder: string,
  value: string
) => text.split(placeholder).join(value);

const toYearMonthDisplay = (yearMonth: string): string => {
  if (yearMonth.length !== 6) return yearMonth;
  return `${yearMonth.slice(0, 4)}年${yearMonth.slice(4, 6)}月`;
};

const parseJapaneseDate = (jaDate: string): string => {
  const matched = jaDate.match(/^(\d{4})年(\d{2})月(\d{2})日$/);
  if (!matched) return "";
  return `${matched[1]}-${matched[2]}-${matched[3]}`;
};

const formatToJapaneseDate = (isoDate: string): string => {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-");
  return `${year}年${month}月${day}日`;
};

/**
 * 個別通知内容確認画面
 */
const NotificationSendModal: FC<Props> = ({
  isOpen,
  onClose,
  notificationDocNo,
  yearMonth,
  agencyName,
  onSent,
}) => {
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [rawBodyTemplate, setRawBodyTemplate] = useState("");
  const [subject, setSubject] = useState("");
  const [computedBody, setComputedBody] = useState("");
  const [body, setBody] = useState("");
  const [deadlineIso, setDeadlineIso] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isConfirmSendAlert, setIsConfirmSendAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [sendResult, setSendResult] = useState<SendResult>(null);
  const [failureMessage, setFailureMessage] = useState(DEFAULT_FAILURE_MESSAGE);
  const deadlineDisplayRef = useRef("");

  const yearMonthDisplay = toYearMonthDisplay(yearMonth);

  useEffect(() => {
    if (!isOpen) return;
    setErrorMessage("");
    setAlertMessage("");
    setIsConfirmSendAlert(false);
    setSendResult(null);
    setFailureMessage(DEFAULT_FAILURE_MESSAGE);
    setIsLoadingTemplate(true);
    setRawBodyTemplate("");
    setComputedBody("");
    setBody("");
    setDeadlineIso("");
    deadlineDisplayRef.current = "";
    (async () => {
      const result = await getMailTemplate(TEMPLATE_KEY);
      try {
        const template = JSON.parse(result);
        setSubject(
          replaceAllPlaceholder(template.subject, "{year_month}", yearMonthDisplay)
        );
        setRawBodyTemplate(template.body);
        setDeadlineIso(parseJapaneseDate(template.default_deadline));
      } catch (e) {
        console.error(e);
        setErrorMessage(
          "テンプレートの取得に失敗しました。時間をおいて再度お試しください。"
        );
      } finally {
        setIsLoadingTemplate(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // 初期表示のみ組み立てる。確認期限の反映は下のuseEffectが担当。
  useEffect(() => {
    if (!rawBodyTemplate) return;
    const deadlineDisplay = formatToJapaneseDate(deadlineIso);
    setComputedBody(
      replaceAllPlaceholder(
        replaceAllPlaceholder(rawBodyTemplate, "{year_month}", yearMonthDisplay),
        "{deadline}",
        deadlineDisplay
      )
    );
    deadlineDisplayRef.current = deadlineDisplay;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawBodyTemplate]);

  // 本文中の期限表記だけを差し替え、それ以外の編集内容は保持する。
  useEffect(() => {
    const oldDeadlineDisplay = deadlineDisplayRef.current;
    if (!oldDeadlineDisplay) return;
    const newDeadlineDisplay = formatToJapaneseDate(deadlineIso);
    if (newDeadlineDisplay === oldDeadlineDisplay) return;
    setBody((prev) =>
      replaceAllPlaceholder(prev, oldDeadlineDisplay, newDeadlineDisplay)
    );
    deadlineDisplayRef.current = newDeadlineDisplay;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deadlineIso]);

  const validate = (): string | null => {
    if (!subject.trim()) return "件名を入力してください。";
    if (subject.length > 100) return "件名の文字数が100文字を超えています。";
    if (!body.trim()) return "本文を入力してください。";
    if (body.length > 2000) return "本文の文字数が2000文字を超えています。";
    if (!deadlineIso) return "確認期限を入力してください。";
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const deadlineDate = new Date(`${deadlineIso}T00:00:00`);
    if (deadlineDate.getTime() <= today.getTime()) {
      return "確認期限は本日より後の日付を指定してください。";
    }
    return null;
  };

  const handleSendClick = () => {
    const validationError = validate();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }
    setErrorMessage("");
    setIsConfirmSendAlert(true);
  };

  const executeSend = async () => {
    setIsSending(true);
    try {
      const res = await postNotificationDocsNotify(
        notificationDocNo,
        yearMonth,
        subject,
        body
      );
      if (res?.statusCode === 200) {
        setSendResult("success");
      } else {
        console.error(res?.message);
        setFailureMessage(res?.message || DEFAULT_FAILURE_MESSAGE);
        setSendResult("failure");
      }
    } catch (e) {
      console.error(e);
      setFailureMessage(DEFAULT_FAILURE_MESSAGE);
      setSendResult("failure");
    } finally {
      setIsSending(false);
      setIsConfirmSendAlert(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleResultClose = () => {
    if (sendResult === "success" && onSent) {
      onSent();
    }
    setSendResult(null);
    handleClose();
  };

  const today = new Date();
  const minDate = new Date(today);
  minDate.setDate(minDate.getDate() + 1);
  const minDateIso = minDate.toISOString().slice(0, 10);

  return (
    <>
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-20"
          onClose={() => {}}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 scale-95"
                enterTo="opacity-100 translate-y-0 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 scale-100"
                leaveTo="opacity-0 translate-y-4 scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-semibold leading-6 text-text border-b border-gray-300 pb-2"
                  >
                    予定額通知
                  </Dialog.Title>

                  <div className="my-6 text-left">
                    {isLoadingTemplate ? (
                      <div className="my-20">
                        <LoadingSpinner />
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-sm font-medium text-text">
                              対象年月
                            </label>
                            <p className="mt-1 text-textSub">
                              {yearMonthDisplay}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text">
                              代理店
                            </label>
                            <p className="mt-1 text-textSub">{agencyName}</p>
                          </div>
                        </div>
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-text">
                            確認期限
                          </label>
                          <input
                            type="date"
                            className="mt-1 block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 text-text leading-6 px-2"
                            min={minDateIso}
                            value={deadlineIso}
                            onChange={(e) => setDeadlineIso(e.target.value)}
                          />
                        </div>
                        <p className="text-sm font-semibold text-text mb-2">
                          送信内容プレビュー
                        </p>
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-text">
                            件名
                          </label>
                          <input
                            type="text"
                            className="mt-1 block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 text-text leading-6 px-2"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            maxLength={100}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text">
                            本文
                          </label>
                          <Textarea
                            row={8}
                            isRequired={true}
                            defaultValue={computedBody}
                            inputValue={body}
                            setInputValue={setBody}
                            placeholder=""
                            maxLength={2000}
                          />
                        </div>
                        {errorMessage && (
                          <p className="mt-4 text-sm text-red-600">
                            {errorMessage}
                          </p>
                        )}
                      </>
                    )}
                  </div>

                  <div className="mt-5 grid grid-flow-row-dense grid-cols-2 gap-6">
                    <Button
                      onClick={handleClose}
                      disabled={isSending}
                      buttonType="normal"
                    >
                      キャンセル
                    </Button>
                    <Button
                      onClick={handleSendClick}
                      disabled={isLoadingTemplate || isSending}
                      isLoading={isSending}
                      buttonType="accent"
                    >
                      送信
                    </Button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      <Alert
        isAlert={isConfirmSendAlert}
        setIsAlert={setIsConfirmSendAlert}
        onClick={executeSend}
        title={"予定額通知"}
        submitText={"送信する"}
        cancelText={"キャンセル"}
        message={alertMessage}
        setMessage={setAlertMessage}
      >
        {yearMonthDisplay}分の通知メールを送信します。
        <br />
        よろしいですか？
      </Alert>

      <Transition.Root show={sendResult !== null} as={Fragment}>
        <Dialog as="div" className="relative z-20" onClose={() => {}}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 scale-95"
                enterTo="opacity-100 translate-y-0 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 scale-100"
                leaveTo="opacity-0 translate-y-4 scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-semibold leading-6 text-text border-b border-gray-300 pb-2"
                  >
                    予定額通知
                  </Dialog.Title>

                  <div className="my-6 flex flex-col items-center text-center">
                    {sendResult === "success" ? (
                      <CheckCircleIcon className="h-12 w-12 text-accent" />
                    ) : (
                      <ExclamationCircleIcon className="h-12 w-12 text-red-500" />
                    )}
                    <p className="mt-4 text-text whitespace-pre-wrap">
                      {sendResult === "success"
                        ? "通知メールの送信を受け付けました。"
                        : failureMessage}
                    </p>
                  </div>

                  <div className="border-t border-gray-300 pt-4 flex justify-between text-left">
                    <span className="text-sm text-gray-500 font-medium">
                      対象年月
                    </span>
                    <span>{yearMonthDisplay}</span>
                  </div>

                  <div className="mt-5 flex justify-center">
                    <Button onClick={handleResultClose} buttonType="accent">
                      閉じる
                    </Button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default NotificationSendModal;
